import React from "react";
import { CustomIcon } from "./CustomIcon";

// ============================================================
// IconTile — Phase 5G tinted-icon-container primitive.
// ============================================================
// A small rounded well behind any CustomIcon, giving it visual
// weight on light backgrounds without changing the PNG itself.
// The tile background pulls from the same semantic palette as
// the Card / Badge primitives, so an icon reading "success" can
// share the exact green-bg used by an ACTIVE badge.
//
// Sizing: outer container = iconSize + 20 (10px padding on each
// side), never smaller than 32px. Radius = --kc-radius-xs.
//
// variant → background token:
//   green  → --kc-green-bg   (active, success, present, completed)
//   amber  → --kc-amber-bg   (visitor, warning, pending, overdue)
//   blue   → --kc-accent-bg  (informational, scheduled, neutral)
//   red    → --kc-red-bg     (urgent, absent, destructive, crisis)
//   grey   → --kc-grey-bg    (inactive, archived, neutral grey)
//   purple → --kc-purple-bg  (guest)
//   none   → transparent (skip the tile background, still applies
//                         padding — useful when the parent already
//                         provides a background)
//
// The icon foreground colour is inherited from the container so
// dark-mode contrast just works: pass `color` to override.
//
// Do NOT wrap sidebar / bottom-nav / More sheet icons in an
// IconTile — those keep their tokenised nav treatments.
// ============================================================

const VARIANT_BG = {
  green:  { background: "var(--kc-green-bg)",  color: "var(--kc-green)"  },
  amber:  { background: "var(--kc-amber-bg)",  color: "var(--kc-amber)"  },
  blue:   { background: "var(--kc-accent-bg)", color: "var(--kc-accent)" },
  red:    { background: "var(--kc-red-bg)",    color: "var(--kc-red)"    },
  grey:   { background: "var(--kc-grey-bg)",   color: "var(--kc-grey)"   },
  purple: { background: "var(--kc-purple-bg)", color: "var(--kc-purple)" },
  none:   { background: "transparent",          color: "var(--kc-sub)"   },
};

export function IconTile({
  name,
  size = 20,
  variant = "blue",
  className = "",
  style = {},
  title,
  ...rest
}) {
  const palette = VARIANT_BG[variant] || VARIANT_BG.blue;
  const box = Math.max(32, size + 20);
  return (
    <span
      className={`kc-icon-tile ${className}`}
      aria-hidden={title ? undefined : "true"}
      role={title ? "img" : "presentation"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: box,
        height: box,
        borderRadius: "var(--kc-radius-xs)",
        flexShrink: 0,
        ...palette,
        ...style,
      }}
      {...rest}
    >
      {typeof name === "string"
        ? <CustomIcon name={name} size={size} title={title} />
        : name}
    </span>
  );
}

export default IconTile;
